import { type FC, useCallback, useRef } from "react";
import { Tab, TabList, TabPanel, Tabs } from "@stubhub/broadway-web";
import type { IconProps } from "@stubhub/broadway-web/icons";
import { CarIcon, DirectionIcon, FestivalIcon, TicketStarOutlineIcon } from "@stubhub/broadway-web/icons/functional";
import { useContent } from "src/shared/components/content";
import type { ContentId } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { scrollWrapperStyle } from "./styles";

export type PassTypeFilter = "festival" | "camping" | "parking" | "shuttle";

/** Default pass-type filter — festival passes are the primary view. */
export const DEFAULT_PASS_TYPE_FILTER: PassTypeFilter = "festival";

interface TabSpec {
  id: PassTypeFilter;
  Icon: FC<IconProps>;
}

const TABS: readonly TabSpec[] = [
  { id: "festival", Icon: TicketStarOutlineIcon },
  { id: "camping", Icon: FestivalIcon },
  { id: "parking", Icon: CarIcon },
  { id: "shuttle", Icon: DirectionIcon },
];

const VALID_TAB_IDS = new Set<string>(TABS.map(({ id }) => id));

/** Content IDs for tab labels — exp 102858 (TMPerformerGAShoppingMode) */
const TAB_CONTENT_IDS = {
  festival: "cid$#4286312#", // "Festival Passes"
  camping: "cid$#4286313#", // "Camping Passes"
  parking: "cid$#4286314#", // "Parking Passes"
  shuttle: "cid$#4286315#", // "Shuttle Passes"
} as const satisfies Record<PassTypeFilter, ContentId>;

interface Props {
  selected: PassTypeFilter | null;
  onSelect: (id: PassTypeFilter) => void;
  availableFilters?: ReadonlySet<PassTypeFilter>;
}

export const GAPassTypeChips: FC<Props> = ({ selected, onSelect, availableFilters }) => {
  const labels: Record<PassTypeFilter, string | null> = {
    festival: useContent(TAB_CONTENT_IDS.festival),
    camping: useContent(TAB_CONTENT_IDS.camping),
    parking: useContent(TAB_CONTENT_IDS.parking),
    shuttle: useContent(TAB_CONTENT_IDS.shuttle),
  };

  const visibleTabs = availableFilters ? TABS.filter(({ id }) => id === "festival" || availableFilters.has(id)) : TABS;

  const scrollRef = useRef<HTMLDivElement>(null);
  const storePageEvent = useStorePageEvent();

  const handleSelectionChange = useCallback(
    (key: string | number) => {
      const id = String(key);
      if (!VALID_TAB_IDS.has(id)) return;
      storePageEvent(
        PE_TYPE.CLICK,
        PE_KEYS.GA_PASS_TYPE_TAB_CLICK,
        JSON.stringify({ selected: id, previous: selected }),
      );
      onSelect(id as PassTypeFilter);
      // Scroll the selected tab into view on mobile
      requestAnimationFrame(() => {
        const container = scrollRef.current;
        if (!container) return;
        const activeTab = container.querySelector('[aria-selected="true"]') as HTMLElement | null;
        activeTab?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      });
    },
    [onSelect, selected, storePageEvent],
  );

  // Hide the tab bar when Festival Passes is the only tab — no point showing
  // a single tab with no alternatives to switch to.
  if (visibleTabs.length <= 1) return null;

  return (
    <div className={scrollWrapperStyle} ref={scrollRef}>
      <Tabs selectedKey={selected} onSelectionChange={handleSelectionChange} width="hug" border>
        <TabList>
          {visibleTabs.map(({ id, Icon }) => (
            <Tab key={id} value={id} icon={Icon}>
              {labels[id]}
            </Tab>
          ))}
        </TabList>
        {visibleTabs.map(({ id }) => (
          <TabPanel key={id} value={id}>
            {null}
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
};
